import { useMemo, type ComponentProps, type ReactNode } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import OutsideEeaBadge from "@/v2-backoffice-client/signIn/outsideEea/OutsideEeaBadge"
import {
  countryFlagEmoji,
  countryName,
} from "@/v2-backoffice-client/signIn/outsideEea/outsideEea.utils"
import Table, {
  type ColumnMetaMap,
  type TableProps,
  createColumns,
  useTableSortUrl,
} from "@/v2-backoffice-client/components/table"
import SharingRiskBadge from "@/v2-backoffice-client/signIn/sharingRisk/SharingRiskBadge"
import type { SharingRiskFragment } from "@/v2-backoffice-client/signIn/__types__/signIn.sharingRisk.fragment"

const TABLE_ID = "sign-in-users-table"
// Worst first: the fine-grained sharing-risk score ranks the review queue.
const INITIAL_SORTING = [{ id: "Score", desc: true }]

function createSignInUserColumns() {
  const columns: ColumnMetaMap<SharingRiskFragment> = {}

  columns.Score = {
    id: "Score",
    label: "Score",
    description:
      "Sharing-risk score for the window. Higher means stronger signs of account sharing. The Sharing Risk level is derived from it.",
    defaultSize: 70,
    filter: { type: "number-range", placeholder: "Score" },
    getValue: (row) => row.Detail.Score,
  }

  columns.Risk = {
    id: "Risk",
    label: "Sharing Risk",
    description:
      "Risk level derived from the score: None, Low, Moderate, High, or Critical. Hover the badge in a row for the full breakdown.",
    defaultSize: 100,
    enableGrouping: true,
    // Rendered inline in the toolbar for quick access. Multi-select so a
    // reviewer can isolate e.g. Moderate + High in one go.
    filter: {
      type: "multi-select",
      placeholder: "Any risk",
      toolbar: true,
      options: [
        { label: "None", value: "1", dotVariant: "secondary" },
        { label: "Low", value: "2", dotVariant: "info" },
        { label: "Moderate", value: "3", dotVariant: "warning" },
        { label: "High", value: "4", dotVariant: "orange" },
        { label: "Critical", value: "5", dotVariant: "destructive" },
      ],
    },
    getValue: (row) => row.Detail.Level,
    renderCell({ row }) {
      // The badge tooltip carries the full risk breakdown (IPs, locations,
      // devices, concurrent sessions, score thresholds) — this is where the
      // "additional details about the risk" live.
      return <SharingRiskBadge risk={row.original.Detail} />
    },
    exportValue({ row }) {
      return row.original.Detail.LevelName
    },
  }

  // Text-filterable, which also makes "User is not any of …" available in the
  // advanced filter builder — i.e. exclude specific users.
  columns.User = {
    id: "User",
    label: "User",
    filter: { type: "text", placeholder: "Filter by user…" },
    getValue: (row) => row.Name || row.Email || "Unknown",
    to({ row }) {
      if (!row.original?.UserId) return
      return `>>?v=user:${row.original.UserId}`
    },
  }

  columns.Email = {
    id: "Email",
    label: "Email",
    defaultHidden: true,
    filter: { type: "text", placeholder: "Filter by email…" },
    getValue: (row) => row.Email ?? "",
  }

  columns.Tenant = {
    id: "Tenant",
    label: "Team",
    description: "The customer organization this user belongs to.",
    enableGrouping: true,
    filter: { type: "text", placeholder: "Filter by team…" },
    getValue: (row) => row.TenantName ?? "",
    to({ row }) {
      if (!row.original?.TenantID) return
      return `>>?v=tenant:${row.original.TenantID}`
    },
  }

  // Cross-border-access flag: a user with any sign-in from outside the EU/EEA.
  // Combined with a High Sharing Risk it is the account-sharing-misuse pattern
  // (e.g. a Norwegian account also signed into from outside the EEA). Rendered
  // as a prominent destructive badge with the non-EEA sign-in count, and a
  // toolbar filter so a reviewer can isolate flagged users in one click.
  columns.OutsideEea = {
    id: "OutsideEea",
    label: "Outside EEA",
    description:
      "The user signed in from outside the EU/EEA at least once in the window. Combined with high sharing risk, this is the account-sharing pattern to review.",
    defaultSize: 110,
    enableGrouping: true,
    filter: {
      type: "select",
      placeholder: "Any region",
      toolbar: true,
      options: [
        { label: "Outside EEA", value: "true" },
        { label: "In EEA only", value: "false" },
      ],
    },
    getValue: (row) => row.IsOutsideEea,
    // Group by the lead non-EEA country so each country forms its own bucket
    // (the boolean flag would lump every country under one mislabelled group).
    // Rows with no outside-EEA sign-in fall under an explicit "In EEA only".
    getGroupingValue: (row) => row.OutsideEeaCountries[0] ?? "",
    renderGroupLabel(value) {
      const code = String(value ?? "")
      if (!code) return "In EEA only"
      const flag = countryFlagEmoji(code)
      return (
        <span className="inline-flex items-center gap-1.5">
          {flag && <span aria-hidden>{flag}</span>}
          {countryName(code)}
        </span>
      )
    },
    renderCell({ row }) {
      return <OutsideEeaBadge countries={row.original.OutsideEeaCountries} />
    },
    exportValue({ row }) {
      return row.original.OutsideEeaCountries.join("; ")
    },
  }

  columns.SignInCount = {
    id: "SignInCount",
    label: "Sign-ins",
    description: "Number of sign-in events for this user in the window.",
    defaultSize: 80,
    filter: { type: "number-range", placeholder: "Sign-ins" },
    getValue: (row) => row.SignInCount,
  }

  columns.IpCount = {
    id: "IpCount",
    label: "IPs",
    description: "Distinct IP addresses the user signed in from in the window.",
    defaultSize: 60,
    filter: { type: "number-range", placeholder: "IPs" },
    getValue: (row) => row.DistinctIpCount,
  }

  columns.Locations = {
    id: "Locations",
    label: "Locations",
    description:
      "Distinct places (city, country) the user signed in from in the window.",
    filter: { type: "text", placeholder: "Filter by location…" },
    getValue: (row) => row.DistinctLocations.join(", "),
    renderCell({ row }) {
      return <TruncatedList items={row.original.DistinctLocations} />
    },
    exportValue({ row }) {
      return row.original.DistinctLocations.join("; ")
    },
  }

  // Distinct ISO country codes the user signed in from in the window (e.g.
  // "NO, PK"). Text-filterable so a reviewer can search a specific country.
  columns.Countries = {
    id: "Countries",
    label: "Countries",
    description:
      "Distinct countries the user signed in from, as ISO codes such as NO or PK.",
    defaultSize: 110,
    filter: { type: "text", placeholder: "Filter by country…" },
    getValue: (row) => row.Countries.join(", "),
    renderCell({ row }) {
      return <TruncatedList items={row.original.Countries} />
    },
    exportValue({ row }) {
      return row.original.Countries.join("; ")
    },
  }

  columns.Devices = {
    id: "Devices",
    label: "Devices",
    description: "Distinct devices (browser and OS) seen in the window.",
    defaultSize: 80,
    defaultHidden: true,
    filter: { type: "number-range", placeholder: "Devices" },
    getValue: (row) => row.Detail.DistinctDevices,
  }

  columns.ConcurrentSessions = {
    id: "ConcurrentSessions",
    label: "Concurrent",
    description:
      "Sign-ins from different IPs close enough in time to overlap. The strongest single sign of account sharing.",
    defaultSize: 90,
    filter: { type: "number-range", placeholder: "Concurrent" },
    getValue: (row) => row.Detail.ConcurrentSessions,
  }

  columns.LastSignInAt = {
    id: "LastSignInAt",
    label: "Last sign-in",
    defaultSize: 100,
    filter: { type: "date-filter" },
    getValue: (row) => row.LastSignInAt ?? "",
    renderCell({ row }) {
      if (!row.original.LastSignInAt) return null
      return <RelativeDate date={row.original.LastSignInAt} />
    },
    exportValue({ row }) {
      return row.original.LastSignInAt
        ? formatDateString(row.original.LastSignInAt)
        : ""
    },
  }

  return createColumns<SharingRiskFragment>({
    enableSorting: true,
    enableHiding: true,
    columns,
  })
}

export type SignInUsersTableProps = Omit<
  TableProps<SharingRiskFragment>,
  "data" | "columns"
> &
  ComponentProps<"div"> & {
    /** Per-user rollup for the window, already ranked worst-first. */
    risks: SharingRiskFragment[]
    isLoading?: boolean
    error?: Error
    onRefresh?(): void
    /** Extra controls rendered in the toolbar, e.g. a period selector. */
    toolbarExtra?: ReactNode
  }

// Per-user view of a sign-in window: one row per user, default-sorted by the
// fine-grained sharing-risk score (worst first). The events behind these
// rollups live in the Events tab / the per-user view sheet.
function SignInUsersTable(props: SignInUsersTableProps) {
  const {
    className,
    risks,
    isLoading,
    error,
    onRefresh,
    toolbarExtra,
    ...tableProps
  } = props

  const columns = useMemo(() => createSignInUserColumns(), [])

  // Bookmarkable sort (FUS-320): the primary sort column round-trips through
  // `?sign-in-users-table-sort=…`, so a shared/reloaded URL restores the view.
  const sortUrl = useTableSortUrl({
    tableId: TABLE_ID,
    initialSorting: INITIAL_SORTING,
  })

  return (
    <Table
      className={cn("sign-in-users-table", className)}
      tableId={TABLE_ID}
      data={risks}
      columns={columns}
      {...sortUrl}
      enableRowSelection={false}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search placeholder="Search users…" />
        {toolbarExtra}
        <Table.ToolbarFilters />

        <Separator orientation="vertical" />
        <Table.ToolbarMenu
          onRefresh={onRefresh}
          exportFileName="sign-in-users"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />
      <Table.Content countLabel="users" isLoading={isLoading} error={error} />
    </Table>
  )
}

export default SignInUsersTable
